import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { subscriptionPlans, leadPricing } from '../data/subscriptionPlans';
import { Check, Zap, Star, TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PricingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      toast({
        title: 'Sign Up Required',
        description: 'Please create a professional account to subscribe.',
      });
      navigate('/');
      return;
    }

    if (user?.userType !== 'professional') {
      toast({
        title: 'Professional Account Required',
        description: 'Only professionals can subscribe to these plans.',
        variant: 'destructive'
      });
      return;
    }

    // If free plan, just show message
    if (plan.id === 'free') {
      toast({
        title: 'Free Plan',
        description: 'This is the default plan for all professionals.',
      });
      return;
    }

    // Redirect to Stripe checkout
    setLoading(plan.id);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API_URL}/api/payments/checkout/session`, {
        packageId: plan.id,
        originUrl: originUrl,
        userId: user.id,
        email: user.email
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to initiate payment. Please try again.',
        variant: 'destructive',
      });
      setLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch(planId) {
      case 'free': return Zap;
      case 'professional': return Star;
      case 'premium': return TrendingUp;
      default: return Check;
    }
  };

  const getPlanColor = (planId) => {
    switch(planId) {
      case 'free': return 'text-gray-600';
      case 'professional': return 'text-orange-600';
      case 'premium': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Pricing for Professionals
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that fits your business. All plans include access to quote requests and customer connections.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {subscriptionPlans.map((plan) => {
            const Icon = getPlanIcon(plan.id);
            const iconColor = getPlanColor(plan.id);
            return (
              <Card 
                key={plan.id} 
                className={plan.popular ? 'border-2 border-orange-500 shadow-xl relative' : 'relative'}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-orange-600 text-white px-4 py-1 text-sm">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Icon className={`h-8 w-8 ${iconColor}`} />
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <p className="text-gray-600 text-sm mt-2">{plan.description}</p>
                </CardHeader>

                <CardContent>
                  <div className="mb-6">
                    <div className="flex items-baseline mb-2">
                      <span className="text-4xl font-bold">${plan.price}</span>
                      <span className="text-gray-600 ml-2">/{plan.billingPeriod}</span>
                    </div>
                    {plan.monthlyEquivalent && (
                      <p className="text-sm text-gray-500">
                        Just ${plan.monthlyEquivalent}/month billed annually
                      </p>
                    )}
                    {plan.savings && (
                      <p className="text-sm text-green-600 font-semibold mt-1">
                        {plan.savings}
                      </p>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Lead Pricing:</p>
                      <p className="text-2xl font-bold text-orange-600">${plan.leadCost}</p>
                      <p className="text-xs text-gray-600">per qualified lead (avg)</p>
                    </div>
                  </div>

                  <Button 
                    onClick={() => handleSelectPlan(plan)}
                    disabled={loading === plan.id}
                    className={`w-full mb-6 ${
                      plan.popular 
                        ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    {loading === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirecting to Checkout...
                      </>
                    ) : (
                      plan.price === 0 ? 'Get Started Free' : 'Checkout Now'
                    )}
                  </Button>

                  <div className="space-y-3 mb-4">
                    <p className="text-sm font-semibold text-gray-900">Includes:</p>
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">How does pay-per-lead work?</h3>
                <p className="text-gray-600 text-sm">
                  You only pay when a customer sends you a quote request. All leads are $15 each, regardless of service category. You can set a monthly budget cap to control costs.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
                <p className="text-gray-600 text-sm">
                  Yes! You can upgrade, downgrade, or cancel your subscription at any time. Annual plans are non-refundable but you can switch plans at renewal.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">What makes a qualified lead?</h3>
                <p className="text-gray-600 text-sm">
                  A qualified lead includes customer contact information, service details, location within your area, and project description. We filter out spam and duplicate requests.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Is there a setup fee?</h3>
                <p className="text-gray-600 text-sm">
                  No setup fees! Free plan is 100% free to start. Paid plans only charge the subscription fee - no hidden costs or setup charges.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-orange-100 text-lg mb-6 max-w-2xl mx-auto">
            Join thousands of professionals who trust Profind.ca to connect with customers
          </p>
          <Button 
            onClick={() => navigate('/')}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-8"
          >
            Get Started Today
          </Button>
        </div>
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default PricingPage;
